import { type Vec2, wrapIndex } from '../core/math';
import { buildBarriers, type Barriers } from './barrier';
import type { LandmarkSpec } from './landmarks';
import { circuitForKey, type RealCircuit } from './circuits';
import { buildBankSeries, buildElevationSeries, buildRunoffKindSeries, type RunoffKind } from './elevation';
import { mulberry32, seedForDate, type Rng } from './rng';
import { sculptSections } from './sections';
import {
  catmullRomClosed,
  convexHull,
  curvature,
  normals,
  offsetRing,
  resampleUniform,
  ringLength,
  selfIntersects,
  smoothClosed,
  smoothSeries,
} from './geometry';

export const SPACING = 2.0; // m between centerline samples
export const TRACK_WIDTH = 8.0; // m, kart-circuit realistic
/** Grand Prix circuits run wider, so a grid of AI karts has room to race side by side. */
export const GP_TRACK_WIDTH = 14.0; // m
export const KERB_WIDTH = 1.2; // m either side
/** Grass runoff past the kerb before the barrier. Shared with the wall renderer
 *  and the clamp in lookup.ts so the visible barrier is exactly where the
 *  physics stops you. Kept generous so a run-off is survivable rather than a
 *  wall you meet a metre after the kerb. */
export const WALL_MARGIN = 10.0; // m
export const TARGET_LENGTH = 820; // m; the ideal lap this produces is asserted
// in tests/track.test.ts rather than restated here, so this comment cannot go
// stale the way a hardcoded lap-time estimate would.
export const MIN_RADIUS = 6.5; // m; a real kart-hairpin radius, not a car one
export const CHECKPOINT_SPACING = 40; // m
/** Fine-grained timing splits for qualifying — 24 equal-distance segments. */
export const MICRO_COUNT = 24;
export const CHECKPOINT_FRACTION = 0.9; // share required for a valid lap

export interface Track {
  seed: number;
  key: string;
  spacing: number;
  width: number;
  kerbWidth: number;
  /** Grass runoff past the kerb before the barrier — see WALL_MARGIN. This is
   *  what the circuit *asks* for; where the layout cannot afford it, see
   *  {@link Track.barriers}, which is what the barrier actually does. */
  wallMargin: number;
  /**
   * Where the barrier really stands, per sample and per side, pinched in from
   * `wallMargin` wherever the full margin would reach another part of the lap
   * or fold through a corner's own centre. Physics and both renderers read
   * this rather than computing an offset of their own, so the armco you see is
   * the armco you hit — see `src/track/barrier.ts`.
   */
  barriers: Barriers;
  /** Kerb colours to cycle along the kerb, when the circuit authors its own.
   *  Absent means the renderer's default red/white — see `RealCircuit.kerbs`. */
  kerbColors?: readonly string[];
  /** Hand-placed scenery that identifies this circuit and no other — see
   *  `src/track/landmarks.ts`. Absent on a generated layout, which has no
   *  identity to carry. */
  landmarks?: readonly LandmarkSpec[];
  length: number;
  center: Vec2[];
  normal: Vec2[];
  left: Vec2[];
  right: Vec2[];
  /** Signed, 1/m. Positive is a left-hand corner. */
  curvature: Float64Array;
  startIndex: number;
  startHeading: number;
  checkpoints: number[];
  /** Centerline index where each of the three timed sectors begins. */
  sectorStarts: number[];
  /** Centerline index where each of the {@link MICRO_COUNT} microsectors begins,
   *  first one on the start/finish line. Used for fine-grained qualifying
   *  telemetry; same equal-distance construction as {@link Track.sectorStarts}. */
  microStarts: number[];
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  /**
   * Metres above the circuit's own datum at each centerline index, already
   * scaled into game metres. Cosmetic only for now — see
   * docs/circuit-fidelity.md §3.3's Tier 0/1/2 split — so nothing in the
   * physics or the speed profile reads this. Only set for a named real
   * circuit that authors `RealCircuit.elevation`; a generated daily layout
   * and an unauthored real circuit are flat, exactly as before.
   */
  height?: Float64Array;
  /** Cross-slope ratio (rise ÷ run) at each centerline index; positive tilts
   *  the inside of the corner down. Same cosmetic-only scope as `height`. */
  bank?: Float64Array;
  /** Run-off surface at each centerline index, beyond the kerb. Only set for
   *  a named real circuit that authors `RealCircuit.runoff`/`runoffDefault`;
   *  everything else keeps the uniform grass `surfaceFor` always used. */
  runoffKind?: RunoffKind[];
}

/** Shorter circuits for a quick session; same generator, smaller target. */
export const LAP_LENGTHS = { standard: TARGET_LENGTH, short: 430 } as const;
export type LapLength = keyof typeof LAP_LENGTHS;

/**
 * Iteratively relax only the points that exceed the curvature cap, leaving the
 * rest of the shape alone. Converges quickly and means we almost never have to
 * throw a seed away.
 */
function limitCurvature(pts: Vec2[], maxK: number, maxIters: number): Vec2[] {
  let cur = pts;
  const n = pts.length;
  for (let iter = 0; iter < maxIters; iter++) {
    const k = smoothSeries(curvature(cur), 2);
    let worst = 0;
    for (let i = 0; i < n; i++) worst = Math.max(worst, Math.abs(k[i]));
    if (worst <= maxK) break;

    const next = cur.map((p) => ({ x: p.x, y: p.y }));
    for (let i = 0; i < n; i++) {
      if (Math.abs(k[i]) <= maxK) continue;
      // Pull this point and its immediate neighbours toward the local average.
      for (let j = -1; j <= 1; j++) {
        const idx = wrapIndex(i + j, n);
        const a = cur[wrapIndex(idx - 1, n)];
        const b = cur[idx];
        const c = cur[wrapIndex(idx + 1, n)];
        const strength = j === 0 ? 0.5 : 0.25;
        next[idx] = {
          x: b.x + strength * ((a.x + c.x) * 0.5 - b.x),
          y: b.y + strength * ((a.y + c.y) * 0.5 - b.y),
        };
      }
    }
    cur = next;
  }
  return cur;
}

function scaleToLength(pts: Vec2[], target: number): Vec2[] {
  const len = ringLength(pts);
  if (len < 1e-6) return pts;
  const s = target / len;
  let cx = 0;
  let cy = 0;
  for (const p of pts) {
    cx += p.x;
    cy += p.y;
  }
  cx /= pts.length;
  cy /= pts.length;
  return pts.map((p) => ({ x: cx + (p.x - cx) * s, y: cy + (p.y - cy) * s }));
}

/**
 * Which shaping rules a circuit is built with.
 *
 * `1` is the layout as first shipped. `2` adds the {@link sculptSections} pass.
 * A circuit's version is fixed by its date (see {@link generatorVersion}) so
 * every past day still regenerates byte-identically forever — the version is
 * chosen once, never inferred from anything that can drift.
 */
export type GeneratorVersion = 1 | 2;

/** Circuits dated on or after this use v2. Everything before is frozen at v1. */
export const GEN_V2_FROM = '2026-09-01';

export function generatorVersion(key: string): GeneratorVersion {
  // Only real ISO date keys are anchored to the freeze line; a shared custom
  // seed ("karting-hotlap:<anything>") is not a historical circuit and just
  // gets the current generator.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return 2;
  return key < GEN_V2_FROM ? 1 : 2;
}

/** One generation attempt. Returns null if the layout is unusable. */
function attempt(
  rng: Rng,
  targetLength: number,
  width: number,
  version: GeneratorVersion,
): Vec2[] | null {
  // 1. Scatter, then hull. The hull guarantees a simple closed loop to start from.
  //    More points than a car circuit would want — kart tracks pack corners
  //    close together (roughly one every 50-80m) rather than stringing them
  //    along long straights.
  const raw: Vec2[] = [];
  const count = rng.int(19, 26);
  for (let i = 0; i < count; i++) {
    raw.push({ x: rng.range(0, 320), y: rng.range(0, 240) });
  }
  const hull = convexHull(raw);
  if (hull.length < 6) return null;

  // 2. Displace edge midpoints in *and* out. A bare hull is convex, which would
  //    mean every corner turns the same way; the concavities this introduces are
  //    what give the circuit both left and right handers.
  const displaced: Vec2[] = [];
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i];
    const b = hull[wrapIndex(i + 1, hull.length)];
    displaced.push(a);

    const mx = (a.x + b.x) * 0.5;
    const my = (a.y + b.y) * 0.5;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const push = rng.range(-0.42, 0.28) * len;
    displaced.push({ x: mx + nx * push, y: my + ny * push });
  }

  // 3. Enforce a minimum separation so the spline has room to breathe. Tighter
  //    than a car circuit's spacing would be, to match how close together kart
  //    corners actually sit.
  const minSep = 16;
  const kept: Vec2[] = [];
  for (const p of displaced) {
    let ok = true;
    for (const q of kept) {
      if (Math.hypot(p.x - q.x, p.y - q.y) < minSep) {
        ok = false;
        break;
      }
    }
    if (ok) kept.push(p);
  }
  if (kept.length < 7) return null;

  // 4. Spline, smooth, normalise scale, then cap curvature.
  let poly = catmullRomClosed(kept, 12);
  poly = smoothClosed(poly, 0.35, 6);
  poly = resampleUniform(poly, SPACING);
  poly = scaleToLength(poly, targetLength);
  if (version >= 2) {
    // Sculpt one or two signature sections in, then re-normalise the length the
    // pushes just changed. Anything the pass makes illegal is caught by the
    // checks below and the seed is retried like any other bad attempt.
    poly = sculptSections(poly, SPACING, rng);
    poly = resampleUniform(poly, SPACING);
    poly = scaleToLength(poly, targetLength);
  }
  poly = limitCurvature(poly, 1 / MIN_RADIUS, 80);
  poly = resampleUniform(poly, SPACING);

  // 5. Reject layouts that fold back on themselves or ended up too tight.
  const clearance = width + KERB_WIDTH * 2 + 3;
  if (selfIntersects(poly, SPACING, clearance)) return null;

  const k = smoothSeries(curvature(poly), 2);
  let worst = 0;
  for (let i = 0; i < k.length; i++) worst = Math.max(worst, Math.abs(k[i]));
  if (worst > 1 / (MIN_RADIUS * 0.82)) return null;

  // Needs a real mix of corners in both directions, not just a token few
  // samples of the weaker direction. 30 samples is 60m at this spacing —
  // roughly one genuine corner's worth, not just a kink on the way past.
  let leftTurns = 0;
  let rightTurns = 0;
  for (let i = 0; i < k.length; i++) {
    if (k[i] > 0.02) leftTurns++;
    if (k[i] < -0.02) rightTurns++;
  }
  if (leftTurns < 30 || rightTurns < 30) return null;

  // A simple closed loop accumulates a full turn of net rotation, so traversed
  // one way it will always have more left-handers than right. Flipping the
  // direction on half of all days is what keeps that from being a permanent bias.
  if (rng.next() < 0.5) poly.reverse();

  return poly;
}

/**
 * Build a named circuit's centerline from its traced control points.
 *
 * The finishing stages are a generated layout's — spline, resample, curvature
 * cap — but there is no scatter, no rejection and no retry: there is exactly
 * one Monza and it has to come out, every time. A corner tighter than
 * {@link MIN_RADIUS} is opened by the curvature limiter instead of throwing the
 * layout away, which is why {@link RealCircuit.length} is chosen to leave the
 * limiter almost nothing to do.
 */
function traceCircuit(def: RealCircuit): Vec2[] {
  let poly = catmullRomClosed(
    def.points.map(([x, y]) => ({ x, y })),
    8,
  );
  poly = scaleToLength(poly, def.length);
  poly = resampleUniform(poly, SPACING);
  // Only enough to take the 0.1 m rounding out of the stored points; anything
  // stronger starts rounding off the chicanes that make these circuits.
  poly = smoothClosed(poly, 0.2, 2);
  poly = limitCurvature(poly, 1 / MIN_RADIUS, 200);
  return resampleUniform(poly, SPACING);
}

/** Longest run of near-straight track, used for the start/finish line. */
function findLongestStraight(k: Float64Array): { start: number; length: number } {
  const n = k.length;
  const straightK = 0.012;
  let best = { start: 0, length: 0 };
  let runStart = -1;
  let runLen = 0;

  // Two laps of the ring so a run spanning the seam is measured correctly.
  for (let i = 0; i < n * 2; i++) {
    const idx = wrapIndex(i, n);
    if (Math.abs(k[idx]) < straightK) {
      if (runStart < 0) runStart = idx;
      runLen++;
      if (runLen > best.length && runLen <= n) best = { start: runStart, length: runLen };
    } else {
      runStart = -1;
      runLen = 0;
    }
  }
  return best;
}

export function generateTrack(
  key: string,
  seedOverride?: number,
  lapLength: LapLength = 'standard',
  width: number = TRACK_WIDTH,
): Track {
  const baseSeed = seedOverride ?? seedForDate(key);
  // A `circuit:<id>` key names a real circuit, which is traced rather than
  // generated: its shape is fixed, so lap length does not apply to it and there
  // is no seed variant to fall back on.
  const circuit = circuitForKey(key);

  let center: Vec2[] | null = null;
  let usedSeed = baseSeed;
  if (circuit) {
    center = traceCircuit(circuit);
  } else {
    const targetLength = LAP_LENGTHS[lapLength];
    const version = generatorVersion(key);
    for (let variant = 0; variant < 60; variant++) {
      usedSeed = (baseSeed + variant * 0x9e3779b1) >>> 0;
      center = attempt(mulberry32(usedSeed), targetLength, width, version);
      if (center) break;
    }
  }
  if (!center) throw new Error(`track generation failed for key ${key}`);

  // Vertical shape and run-off character, for a named real circuit that
  // authors them — see docs/circuit-fidelity.md. `factor` folds the
  // circuit's own horizontal shrink together with its `verticalScale`
  // (§2: elevation needs a *second*, usually smaller, scale-down on top of
  // the horizontal one, or a real gradient blocks the driver's own view).
  let height: Float64Array | undefined;
  let bank: Float64Array | undefined;
  let runoffKind: RunoffKind[] | undefined;
  let wallMargin = WALL_MARGIN;
  let kerbColors: readonly string[] | undefined;
  let landmarks: readonly LandmarkSpec[] | undefined;
  if (circuit) {
    const factor = (circuit.length / circuit.realLength) * (circuit.verticalScale ?? 1);
    height = buildElevationSeries(circuit.elevation, center.length, factor);
    bank = buildBankSeries(circuit.banking, center.length);
    if (circuit.runoff || circuit.runoffDefault) {
      runoffKind = buildRunoffKindSeries(circuit.runoff, center.length, circuit.runoffDefault);
    }
    wallMargin = circuit.wallMargin ?? WALL_MARGIN;
    kerbColors = circuit.kerbs;
    landmarks = circuit.landmarks;
  }

  const norm = normals(center);
  const half = width / 2;
  const left = offsetRing(center, norm, half);
  const right = offsetRing(center, norm, -half);
  const k = smoothSeries(curvature(center), 2);

  // A real circuit's line is where the real one is — index 0 of its traced
  // points, which the pipeline above preserves. A generated layout has to find
  // its own, and sits the line a little into the straight so there is room to
  // accelerate away.
  let startIndex = 0;
  if (!circuit) {
    const straight = findLongestStraight(k);
    startIndex = wrapIndex(straight.start + Math.floor(straight.length * 0.25), center.length);
  }
  const ahead = center[wrapIndex(startIndex + 1, center.length)];
  const behind = center[wrapIndex(startIndex - 1, center.length)];
  const startHeading = Math.atan2(ahead.y - behind.y, ahead.x - behind.x);

  const everyN = Math.max(1, Math.round(CHECKPOINT_SPACING / SPACING));
  const checkpoints: number[] = [];
  for (let i = 0; i < center.length; i += everyN) {
    checkpoints.push(wrapIndex(startIndex + i, center.length));
  }

  // Three equal-distance sectors from the line. Equal distance rather than equal
  // time keeps the split stable when weather or tyres change the pace.
  const sectorStarts = [0, 1, 2].map((s) =>
    wrapIndex(startIndex + Math.round((s * center.length) / 3), center.length),
  );

  // Microsectors — the same equal-distance split as sectors, just finer. First
  // boundary is the line itself, so a completed lap fills every entry.
  const microStarts: number[] = [];
  for (let m = 0; m < MICRO_COUNT; m++) {
    microStarts.push(wrapIndex(startIndex + Math.round((m * center.length) / MICRO_COUNT), center.length));
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of [...left, ...right]) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  const barriers = buildBarriers(center, norm, width, KERB_WIDTH, wallMargin, SPACING);

  return {
    seed: usedSeed,
    key,
    spacing: SPACING,
    width,
    kerbWidth: KERB_WIDTH,
    wallMargin,
    barriers,
    kerbColors,
    landmarks,
    length: center.length * SPACING,
    center,
    normal: norm,
    left,
    right,
    curvature: k,
    startIndex,
    startHeading,
    checkpoints,
    sectorStarts,
    microStarts,
    bounds: { minX, minY, maxX, maxY },
    height,
    bank,
    runoffKind,
  };
}
